// src/services/formUtils.js
//
// Form input value parsers with explicit empty/invalid handling.
// Replaces 57-site parseInt(e.target.value) / parseFloat(e.target.value)
// NaN-poisoning antipattern catalogued in 8m.4 audit. Migration sweeps
// in commits 8m.5–8m.10.
//
// API surface (3 behaviors × 2 types = 6 exports):
//   parseIntOrNull / parseIntOrPrev / parseIntOrDefault
//   parseFloatOrNull / parseFloatOrPrev / parseFloatOrDefault
//
// Edge case semantics (verified by sibling test file):
//   ''  / null / undefined  → fallback (null / prev / default)
//   '   ' (whitespace-only)  → fallback (parseInt → NaN → fallback)
//   '0' / 0                  → 0 (KEYSTONE: explicitly preserved by isNaN guard;
//                                 fixes the Pattern B `|| default` 0-as-missing bug)
//   '-5'                     → -5 (sign preserved, no enforcement)
//   '15abc'                  → 15 (parseInt partial-parse inherited per JS standard)
//   '  15  '                 → 15 (parseInt strips leading whitespace)
//   '1e10' (parseInt)        → 1   (parseInt stops at 'e')
//   '1e10' (parseFloat)      → 1e10 (parseFloat handles exponential notation)

/**
 * Parse a form input value as an integer; return null for empty/invalid input.
 * Use for fields where empty-input is a valid 'cleared' state semantic.
 * Downstream save handler should validate null per 8m.3 precedent before
 * persisting.
 */
export const parseIntOrNull = (rawValue) => {
  if (rawValue === '' || rawValue === null || rawValue === undefined) return null;
  const parsed = parseInt(rawValue, 10);
  return isNaN(parsed) ? null : parsed;
};

/**
 * Parse a form input value as an integer; fall back to a previous value for
 * empty/invalid input. Use for fields where the user's previous state should
 * persist through invalid edits.
 */
export const parseIntOrPrev = (rawValue, prev) => {
  if (rawValue === '' || rawValue === null || rawValue === undefined) return prev;
  const parsed = parseInt(rawValue, 10);
  return isNaN(parsed) ? prev : parsed;
};

/**
 * Parse a form input value as an integer; fall back to a static default for
 * empty/invalid input. Replaces the `parseInt(x) || default` antipattern
 * which silently coerced 0 to default; this canonical correctly preserves 0
 * via explicit isNaN guard.
 */
export const parseIntOrDefault = (rawValue, defaultValue) => {
  if (rawValue === '' || rawValue === null || rawValue === undefined) return defaultValue;
  const parsed = parseInt(rawValue, 10);
  return isNaN(parsed) ? defaultValue : parsed;
};

/**
 * Parse a form input value as a float; return null for empty/invalid input.
 * Same null-on-empty semantic as parseIntOrNull, decimal-preserving.
 */
export const parseFloatOrNull = (rawValue) => {
  if (rawValue === '' || rawValue === null || rawValue === undefined) return null;
  const parsed = parseFloat(rawValue);
  return isNaN(parsed) ? null : parsed;
};

/**
 * Parse a form input value as a float; fall back to a previous value for
 * empty/invalid input. Decimal-preserving variant of parseIntOrPrev.
 */
export const parseFloatOrPrev = (rawValue, prev) => {
  if (rawValue === '' || rawValue === null || rawValue === undefined) return prev;
  const parsed = parseFloat(rawValue);
  return isNaN(parsed) ? prev : parsed;
};

/**
 * Parse a form input value as a float; fall back to a static default for
 * empty/invalid input. Decimal-preserving variant of parseIntOrDefault.
 * Same 0-preservation guarantee.
 */
export const parseFloatOrDefault = (rawValue, defaultValue) => {
  if (rawValue === '' || rawValue === null || rawValue === undefined) return defaultValue;
  const parsed = parseFloat(rawValue);
  return isNaN(parsed) ? defaultValue : parsed;
};
